import React, { useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Título exibido no header padrão (mesmo estilo do carrinho). */
  title?: ReactNode;
  /** Quando false, o header padrão não é renderizado (conteúdo controla tudo). */
  showHeader?: boolean;
  maxWidth?: string;
  zIndex?: number;
  preventScroll?: boolean;
  children: ReactNode;
}

// Mobile: abre deslizando de baixo (bottom sheet). Desktop: modal centralizado.
const Overlay = styled.div<{ zIndex?: number }>`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: ${props => props.zIndex || 1050};
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 992px) {
    align-items: flex-end;
  }
`;

const Sheet = styled.div<{ maxWidth?: string }>`
  background-color: #fff;
  width: 100%;
  max-width: ${props => props.maxWidth || '500px'};
  max-height: 90vh;
  border-radius: 20px;
  margin: 1.75rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 992px) {
    margin: 0;
    max-width: 100%;
    max-height: 92vh;
    border-radius: 20px 20px 0 0;
    animation: sheetSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  @keyframes sheetSlideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
`;

// Header no mesmo padrão do carrinho: título à esquerda, X (cor do tema) à direita.
const Header = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #edf2f7;
  background-color: #fff;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: #2d3748;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.primary || '#e74c3c'};

  i {
    font-size: 1.4rem;
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  showHeader = true,
  maxWidth,
  zIndex,
  preventScroll = true,
  children,
}) => {
  useEffect(() => {
    if (preventScroll && isOpen) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, preventScroll]);

  if (!isOpen) return null;

  const sheet = (
    <Overlay zIndex={zIndex} onClick={onClose}>
      <Sheet maxWidth={maxWidth} onClick={(e) => e.stopPropagation()}>
        {showHeader && (
          <Header>
            <Title>{title}</Title>
            <CloseButton type="button" onClick={onClose} aria-label="Fechar">
              <i className="fas fa-times" />
            </CloseButton>
          </Header>
        )}
        <Body>{children}</Body>
      </Sheet>
    </Overlay>
  );

  return createPortal(sheet, document.body);
};

export default BottomSheet;
